import { useEffect, useState } from 'react';
import { clsx } from 'clsx';

import { DatePicker, DateTimePicker } from '@atom/mui';
import { DataType } from '@atom/types/dataType';
import { TaskField } from '@atom/types/task';
import { PREVIEW_FIELD_WIDTH } from '@atom/utilities/taskUtilities';

import classes from './taskFields.module.css';

interface Props {
  field: TaskField;
  onChange: (fieldId: string, value: any) => void;
  isDisabled: boolean;
  error?: boolean;
  helperText?: string;
  onClick?: () => void;
  taskId?: string;
}

const containerWidth = {
  maxWidth: PREVIEW_FIELD_WIDTH,
};

const TaskDateField = ({
  field,
  taskId,
  onChange,
  isDisabled,
  error = false,
  helperText,
  onClick,
}: Props) => {
  const [value, setValue] = useState<Date>(
    field.value ? new Date(field.value) : null,
  );

  useEffect(() => {
    setValue(field.value ? new Date(field.value) : null);
  }, [taskId, field?.id]);

  const handleChange = (date?: Date) => {
    setValue(date);

    // date was removed entirely
    if (!date) {
      onChange(field.id, '');
    } else if (!Number.isNaN(date.valueOf())) {
      onChange(field.id, date.valueOf());
    }
  };

  const title = field.required ? `* ${field.title}` : field.title;

  const props = {
    value,
    helperText,
    error,
    onClick,
    onChange: handleChange,
    disabled: isDisabled,
    style: { maxWidth: PREVIEW_FIELD_WIDTH },
    'data-cy': `taskField_${field.title}_DateInput`,
  };

  const content =
    field.dataType === DataType.DATE ? (
      <DatePicker {...props} />
    ) : (
      <DateTimePicker {...props} />
    );

  return (
    <div
      onClick={onClick}
      className={classes.fieldContainer}
      style={containerWidth}
    >
      <div
        className={clsx(classes.fieldTitle, error && classes.fieldTitleError)}
      >
        {title}
      </div>
      {content}
    </div>
  );
};

export default TaskDateField;
